import React, { useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { fetchWrapper } from '../utils/apiHandlers'
import LoadingDotsComp from '../components/LoadingDotsComp'
import logo from '../assets/logo.png'
import theme from '../theme'

interface Message {
  from: 'bot' | 'user'
  text: string
}

type PrepState = 'checking' | 'crawling' | 'ready' | 'error'

const DemoPage: React.FC = () => {
  const { userid } = useParams<{ userid: string }>()
  const [messages, setmessages] = useState<Message[]>([
    { from: 'bot', text: "Hi! I've been trained on this business's own website. Ask me anything a customer might ask." },
  ])
  const [question, setquestion] = useState('')
  const [waiting, setwaiting] = useState(false)
  const [prepstate, setprepstate] = useState<PrepState>('checking')
  const [preperror, setpreperror] = useState('')
  const [businessname, setbusinessname] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  // The greeting names the actual business once we know it, instead of staying generic -
  // but only while it's still the untouched default message (never overwrite a real conversation).
  const applyBusinessName = (name: string) => {
    if (!name || name === businessname) return
    setbusinessname(name)
    setmessages((m) => (m.length === 1 && m[0].from === 'bot'
      ? [{ from: 'bot', text: `Hi! I've been trained on ${name}'s own website. Ask me anything a customer might ask.` }]
      : m))
  }

  useEffect(() => {
    let cancelled = false
    let pollTimer: any = null

    const poll = async () => {
      try {
        let response: any = await fetchWrapper.post('/api/leaddemo/status', { userid })
        if (cancelled) return
        if (response.status !== 200) {
          setprepstate('error')
          setpreperror(response.msg || 'This demo link is no longer active.')
          return
        }
        applyBusinessName(response.businessname)
        if (response.crawlstatus === 'ready') {
          setprepstate('ready')
        } else if (response.crawlstatus === 'error') {
          setprepstate('error')
          setpreperror("We couldn't read that website. Please try again later.")
        } else {
          setprepstate('crawling')
          pollTimer = setTimeout(poll, 4000)
        }
      } catch (error) {
        if (cancelled) return
        setprepstate('error')
        setpreperror('Something went wrong, please try again in a moment.')
      }
    }

    const start = async () => {
      try {
        let response: any = await fetchWrapper.post('/api/leaddemo/prepare', { userid })
        if (cancelled) return
        if (response.status !== 200) {
          setprepstate('error')
          setpreperror(response.msg || 'This demo link is no longer active.')
          return
        }
        applyBusinessName(response.businessname)
        if (response.crawlstatus === 'ready') {
          setprepstate('ready')
        } else {
          setprepstate('crawling')
          pollTimer = setTimeout(poll, 4000)
        }
      } catch (error) {
        if (cancelled) return
        setprepstate('error')
        setpreperror('Something went wrong, please try again in a moment.')
      }
    }

    start()
    return () => {
      cancelled = true
      if (pollTimer) clearTimeout(pollTimer)
    }
  }, [userid])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, waiting])

  useEffect(() => {
    document.title = businessname ? `${businessname} — Chatbot Demo` : 'Your AI Chatbot Demo'
  }, [businessname])

  const ask = async () => {
    let q = question.trim()
    if (!q || waiting) return
    // last few turns already on screen, so a follow-up ("when will I get it") still resolves
    let history = messages.reduce((pairs: { q: string; a: string }[], m, i, arr) => {
      if (m.from === 'user' && arr[i + 1]?.from === 'bot') pairs.push({ q: m.text, a: arr[i + 1].text })
      return pairs
    }, []).slice(-3)
    setmessages((m) => [...m, { from: 'user', text: q }])
    setquestion('')
    setwaiting(true)
    try {
      let response: any = await fetchWrapper.post('/api/leaddemo', { userid, question: q, history })
      setmessages((m) => [...m, { from: 'bot', text: response.answer }])
    } catch (error) {
      setmessages((m) => [...m, { from: 'bot', text: 'Something went wrong, please try again in a moment.' }])
    }
    setwaiting(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') ask()
  }

  return (
    <PageContainer>
      <Card>
        <CardHeader>
          <img src={logo} width={40} height={40} alt="" />
          <div>
            <Title>{businessname ? `${businessname} — AI Chatbot Demo` : 'Your AI Chatbot Demo'}</Title>
            <Subtitle>{businessname ? `Powered by miamigo.ai — trained on ${businessname}'s own website content` : 'Powered by miamigo.ai — trained on your own website content'}</Subtitle>
          </div>
        </CardHeader>

        {prepstate === 'ready' ? (
          <>
            <ChatWindow>
              {messages.map((m, i) => (
                <Bubble key={i} $fromUser={m.from === 'user'}>
                  {m.text}
                </Bubble>
              ))}
              {waiting && (
                <Bubble $fromUser={false}>
                  <LoadingDotsComp />
                </Bubble>
              )}
              <div ref={bottomRef} />
            </ChatWindow>

            <InputRow>
              <Input
                placeholder="Type your question..."
                value={question}
                maxLength={250}
                onChange={(e) => setquestion(e.target.value)}
                onKeyDown={onKeyDown}
              />
              <SendButton onClick={ask} disabled={waiting || !question.trim()}>
                Send
              </SendButton>
            </InputRow>
          </>
        ) : prepstate === 'error' ? (
          <PrepStateBox>
            <PrepText>{preperror}</PrepText>
          </PrepStateBox>
        ) : (
          <PrepStateBox>
            <LoadingDotsComp />
            <PrepText>Reading your website and setting up your personalized chatbot - this can take a minute or two...</PrepText>
          </PrepStateBox>
        )}

        <Footer>
          Like what you see? <a href="/register">Get this on your own website →</a>
        </Footer>
      </Card>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 85vh;
  padding: 40px 16px;
  background: ${theme.colors.background};
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
  background: ${theme.colors.surface};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.raised};
  overflow: hidden;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: ${theme.colors.primaryDark};
  color: #fff;
`

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
`

const Subtitle = styled.div`
  font-size: 13px;
  opacity: 0.85;
`

const PrepStateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 30px;
  min-height: 300px;
  text-align: center;
`

const PrepText = styled.div`
  font-size: 14px;
  color: ${theme.colors.textMuted};
  max-width: 320px;
  line-height: 1.6;
`

const ChatWindow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  height: 420px;
  overflow-y: auto;
`

const Bubble = styled.div<{ $fromUser: boolean }>`
  align-self: ${(p) => (p.$fromUser ? 'flex-end' : 'flex-start')};
  background: ${(p) => (p.$fromUser ? theme.colors.primary : theme.colors.primarySoft)};
  color: ${(p) => (p.$fromUser ? '#fff' : theme.colors.text)};
  padding: 10px 14px;
  border-radius: ${(p) => (p.$fromUser ? '14px 14px 2px 14px' : '14px 14px 14px 2px')};
  max-width: 80%;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
`

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid ${theme.colors.border};
`

const Input = styled.input`
  flex: 1;
  padding: 10px 14px;
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.border};
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${theme.colors.primary};
  }
`

const SendButton = styled.button`
  padding: 10px 20px;
  border-radius: ${theme.radius.md};
  border: none;
  background: ${theme.colors.primary};
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const Footer = styled.div`
  text-align: center;
  font-size: 13px;
  padding: 14px;
  color: ${theme.colors.textMuted};
  border-top: 1px solid ${theme.colors.border};
  a {
    color: ${theme.colors.primary};
    font-weight: 600;
    text-decoration: none;
  }
`

export default DemoPage
